<template>
    <div>
        <!-- Line -->
        <line-attribute v-if="!isWfsWithGraphicalLayer && $_showLineStyle"
                        :settings="getSettings"
                        :name="`name_${tabSelection}_${localThemeStyle.id}`"
                        :geometryTypeArray="geometryTypeArray"
                        :isIconSelected="$_isIconSelected"
                        :showSvgStyler="isStyleTypeDefault"
                        :showSaveDefaultButton="showSaveDefaultButton"
                        @update="$_updateSettings($event)">
        </line-attribute>
        <!-- Icon -->
        <div v-if="!isWfsWithGraphicalLayer && hasPointGeometry"
             class="form-inline style-item">
            <div class="form-group align-items-start">
                <label class="custom style-label-md">{{ $t('icon') }}</label>
                <image-loader :id="`name_${localThemeStyle.id}_${tabSelection}`"
                              :name="`name_${localThemeStyle.id}_${tabSelection}`"
                              v-model="localThemeStyle[`icon${tabSelection}`]"
                              :imgExtendedClass="imgExtendedClass"></image-loader>
            </div>
        </div>
        <!-- Svg styler for icon -->
        <div v-if="isSvgIcon" class="form-inline">
            <label class="style-label-md"></label>
            <svg-styler :source="localThemeStyle[`icon${tabSelection}`]"
                        :hasPointGeometry="hasPointGeometry"
                        :settings="getIconSettings"
                        @update="$_updateIconSettings($event)">
            </svg-styler>
        </div>
        <!-- Style for WfsWmsPolgygonLayer -->
        <div v-if="isWfsWmsPolgygonLayer">
            <div class="form-inline style-item">
                <label :for="`${tabSelection}_${localThemeStyle.id}_stroke`"
                       class="custom style-label-md text-attribute">{{ $t('labelStrokeColorNoFill') }}</label>
                <div class="form-group">
                    <div class="input-group cp-control">
                        <color-picker :id="`${tabSelection}_${localThemeStyle.id}_stroke`"
                                      name="`${tabSelection}_${localThemeStyle.id}_stroke`"
                                      v-model="localThemeStyle[`strokeColorDefault`]"></color-picker>
                    </div>
                </div>
            </div>
            <div class="form-inline style-item">
                <div class="form-group">
                    <label :for="`${tabSelection}_${localThemeStyle.id}_width`"
                           class="custom style-label-md text-attribute">{{ $t('labelStrokeWidthNoFill') }}</label>
                    <b-form-select :id="`${tabSelection}_${localThemeStyle.id}_width`"
                                   v-model="localThemeStyle[`strokeWidthDefault`]"
                                   :options="
                            $_convertToValueTextPair(
                                layerStyleConst.stroke_width
                            )
                        ">
                    </b-form-select>
                </div>
            </div>
        </div>
        <!-- Text -->
        <div v-if="
             isStyleTypeDefault &&
             textAttributeOptions &&
             textAttributeOptions.length > 0 &&
             isLabelAllowed
            "
            >
            <div class="form-inline">
                <text-attribute v-bind:textAttributeOptions.sync="textAttributeOptions"
                                v-bind:textAttribute.sync="
                                    localThemeStyle[`textAttribute${tabSelection}`]"
                                :themeStyle="localThemeStyle"
                                :name="`${tabSelection}_${localThemeStyle.id}_name`"
                                :showTextAttribute="showTextAttribute"
                                :isFeedback="isFeedback">
                </text-attribute>
            </div>
        </div>
    </div>
</template>

<script>
    import {
        NEDGLOBE_VIEWER,
        FEEDBACKS_GEOMETRY_TYPE_CODES,
        LAYER_STYLE
    } from '../../../constants';
    import { ICON_UNITS } from '../../../constants/api';
    import {
        convertToValueTextPair,
        isSvgBase64Image
    } from '../../../services/utilities';
    import ColorPicker from '../../../components/ColorPicker.vue';
    import textAttribute from './TextAttribute.vue';
    import lineAttribute from './LineAttribute.vue';
    import imageLoader from '../../../components/ImageLoader.vue';
    import SvgStyler from '../../../components/BaseComponents/SvgStyler.vue';

    export default {
        name: 'styleTab',

        components: {
            'color-picker': ColorPicker,
            'text-attribute': textAttribute,
            'line-attribute': lineAttribute,
            'image-loader': imageLoader,
            'svg-styler': SvgStyler
        },

        props: {
            themeStyle: { type: Object, required: false },
            textAttributeOptions: {
                type: Array,
                required: false,
                default: () => []
            },
            tabSelection: { type: String, required: true, default: 'Default' },
            geometryTypeArray: { type: Array, required: false, default: () => [] },
            name: { type: String, required: false, default: '' },
            isWfsWithGraphicalLayer: {
                type: Boolean,
                required: false,
                default: false
            },
            showTextAttribute: { type: Boolean, required: false, default: true },
            showSaveDefaultButton: {
                type: Boolean,
                required: false,
                default: false
            },
            isFeedback: { type: Boolean, required: false, default: false },
            isLabelAllowed: { type: Boolean, required: false, default: false }
        },

        data() {
            return {
                // we create a local variable for themestyle: then eslint dont complains
                // about "Unexpected mutation of "themeStyle" prop vue/no-mutating-props"
                localThemeStyle: this.themeStyle,
                layerStyleConst: LAYER_STYLE,
                localSettings: null
            };
        },

        computed: {
            getSettings() {
                return {
                    fillColor:
                        this.localThemeStyle[`fillColor${this.tabSelection}`] || null,
                    strokeColor:
                        this.localThemeStyle[`strokeColor${this.tabSelection}`] || null,
                    strokeWidth:
                        this.localThemeStyle[`strokeWidth${this.tabSelection}`] || null,
                    pointSize:
                        this.localThemeStyle[`pointSize${this.tabSelection}`] || null,
                    nameSvg: this.localThemeStyle['nameSvg'] || null,
                    fillSvg: this.localThemeStyle['fillSvg'] || '#ffffff',
                    fillOpacitySvg: this.localThemeStyle['fillOpacitySvg'] || 1,
                    strokeSvg: this.localThemeStyle['strokeSvg'] || '#000000',
                    strokeWidthSvg: this.localThemeStyle['strokeWidthSvg'] || 1,
                    strokeOpacitySvg: this.localThemeStyle['strokeOpacitySvg'] || 1,
                    sizeSvg: this.localThemeStyle['sizeSvg'] || 15,
                    rotationSvg: this.localThemeStyle['rotationSvg'] || 0
                };
            },
            getIconSettings() {
                return {
                    fillSvg:
                        this.localThemeStyle[`iconFill${this.tabSelection}`] ||
                        '#ffffff',
                    fillOpacitySvg:
                        this.localThemeStyle[`iconFillOpacity${this.tabSelection}`] || 1,
                    strokeSvg:
                        this.localThemeStyle[`iconStroke${this.tabSelection}`] ||
                        '#000000',
                    strokeWidthSvg:
                        this.localThemeStyle[`iconStrokeWidth${this.tabSelection}`] || 1,
                    strokeOpacitySvg:
                        this.localThemeStyle[`iconStrokeOpacity${this.tabSelection}`] ||
                        1,
                    sizeSvg: this.localThemeStyle[`iconSize${this.tabSelection}`] || 15,
                    sizeUnits:
                        this.localThemeStyle[`iconSizeUnits${this.tabSelection}`] ||
                        ICON_UNITS.pixels
                };
            },
            // If the layer is WFS with graphicsl layer and has a polgyon geometry only
            // then we need to be able to set a stropke color for the polygon in case
            // user hides the fillvcolor hidden in mobile.
            isWfsWmsPolgygonLayer() {
                return this.isWfsWithGraphicalLayer && this.hasPolygonGeometryOnly;
            },
            isStyleTypeDefault() {
                return (
                    this.tabSelection === NEDGLOBE_VIEWER.styleDisplayTypes.default
                );
            },
            hasPointGeometry() {
                return this.geometryTypeArray.includes(
                    FEEDBACKS_GEOMETRY_TYPE_CODES.POINT
                );
            },
            hasPointGeometryOnly() {
                return (
                    this.geometryTypeArray.length > 0 &&
                    this.geometryTypeArray.every((item) =>
                        [FEEDBACKS_GEOMETRY_TYPE_CODES.POINT].includes(item)
                    )
                );
            },
            hasPolygonGeometryOnly() {
                return (
                    this.geometryTypeArray.length > 0 &&
                    this.geometryTypeArray.every((item) =>
                        [FEEDBACKS_GEOMETRY_TYPE_CODES.POLYGON].includes(item)
                    )
                );
            },
            $_isIconSelected() {
                return !!this.localThemeStyle[`icon${this.tabSelection}`];
            },
            $_showLineStyle() {
                return !this.hasPointGeometryOnly || !this.$_isIconSelected;
            },
            isSvgIcon() {
                return (
                    this.$_isIconSelected &&
                    isSvgBase64Image(this.localThemeStyle[`icon${this.tabSelection}`])
                );
            },
            imgExtendedClass() {
                return this.isSvgIcon ? 'd-none' : 'pb-2';
            }
        },
        methods: {
            $_updateSettings(event) {
                this.localThemeStyle[`fillColor${this.tabSelection}`] = event.fillColor;
                this.localThemeStyle[`strokeColor${this.tabSelection}`] =
                    event.strokeColor;
                this.localThemeStyle[`strokeWidth${this.tabSelection}`] =
                    event.strokeWidth;
                this.localThemeStyle[`pointSize${this.tabSelection}`] = event.pointSize;

                this.localThemeStyle['nameSvg'] = event.nameSvg;
                this.localThemeStyle['fillSvg'] = event.fillSvg;
                this.localThemeStyle['fillOpacitySvg'] = event.fillOpacitySvg;
                this.localThemeStyle['strokeSvg'] = event.strokeSvg;
                this.localThemeStyle['strokeWidthSvg'] = event.strokeWidthSvg;
                this.localThemeStyle['strokeOpacitySvg'] = event.strokeOpacitySvg;
                this.localThemeStyle['sizeSvg'] = event.sizeSvg;
                this.localThemeStyle['rotationSvg'] = event.rotationSvg;
            },
            $_updateIconSettings(event) {
                this.localThemeStyle[`iconFill${this.tabSelection}`] = event.fillSvg;
                this.localThemeStyle[`iconFillOpacity${this.tabSelection}`] =
                    event.fillOpacitySvg;
                this.localThemeStyle[`iconStroke${this.tabSelection}`] = event.strokeSvg;
                this.localThemeStyle[`iconStrokeWidth${this.tabSelection}`] =
                    event.strokeWidthSvg;
                this.localThemeStyle[`iconStrokeOpacity${this.tabSelection}`] =
                    event.strokeOpacitySvg;
                this.localThemeStyle[`iconSize${this.tabSelection}`] = event.sizeSvg;
                this.localThemeStyle[`iconSizeUnits${this.tabSelection}`] =
                    event.sizeUnits;
            },
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            }
        }
    };
</script>
